import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Tab,
  Tabs,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import {
  getSeasonCutoffIndex,
  getSeasonWeeksForSpecies,
} from 'common/src/utils/seasonWeeks';
import {
  createSharedReports,
  getSharedReportUrl,
  type SharedReport,
} from 'common/src/api/sharedReports';
import Header from '../../components/layouts/Header';
import {
  Alert,
  BackButton,
  EmptyState,
  LoadingState,
  PageLayout,
} from '../../components/ui';
import ReportDocument from '../../components/client-reports/ReportDocument';
import { useDownloadReportPdf } from '../../components/client-reports/downloadReportPdf';
import SharedReportFrame, {
  SharedReportCard,
} from '../../components/client-reports/SharedReportFrame';
import { ReportLayoutContext } from '../../components/client-reports/reportLayout';
import { useReportFormat } from '../../components/client-reports/useReportFormat';
import {
  buildClientReportSnapshot,
  listReportDepartureWeeks,
  loadClientReportData,
  type ClientReportData,
} from '../../services/clientReportsService';
import { weekOfDate } from 'common/src/utils/commercialProgram';
import {
  currentWeekStart,
  defaultDepartureWeek,
  localDay,
  staleShipmentsBase,
} from '../../utils/client-reports/build-shipments-section';
import type {
  ClientReportKind,
  ReportPeriod,
  ReportSectionType,
} from '../../utils/client-reports/types';

type LinkMode = 'perReceiver' | 'single';
type PreviewDevice = 'desktop' | 'mobile';

type ReportKind = ClientReportKind;

// Cada informe trae sus secciones; se eligen las que van. Los datos son
// siempre de la temporada a la fecha.
const REPORT_KINDS: Array<{
  key: ReportKind;
  label: string;
  parts: Array<{ key: ReportSectionType; label: string; description: string }>;
}> = [
  {
    key: 'shipments',
    label: 'Informe de Despachos',
    parts: [
      {
        key: 'shipmentsToDate',
        label: 'Despachos acumulados a la fecha',
        description:
          'Embarques y cajas de la temporada, arribado y en tránsito, cumplimiento del plan comercial, puertos de destino y mix por variedad.',
      },
      {
        key: 'upcomingArrivals',
        label: 'Arribos próximos 7 días',
        description:
          'Embarques que llegan en los próximos 7 días, por día y por nave, con puerto de destino y estado de la ETA.',
      },
      {
        key: 'recentShipments',
        label: 'Últimos embarques despachados',
        description:
          'Contenedores que zarparon la última semana, con nave, destino, fechas y estado.',
      },
    ],
  },
  {
    key: 'pendingSettlements',
    label: 'Liquidaciones por recibir',
    parts: [
      {
        key: 'pendingSettlementsSummary',
        label: 'Resumen',
        description:
          'Embarques sin liquidar críticos (más de 45 días desde la ETA), vencidos (30 a 45) y por vencer en los próximos 7 días.',
      },
      {
        key: 'settlementsByVessel',
        label: 'Por nave',
        description:
          'Lo sin liquidar agrupado por nave o vuelo: embarques, cajas, estado más atrasado y días desde la ETA.',
      },
      {
        key: 'overdueSettlements',
        label: 'Detalle por contenedor: críticas y vencidas',
        description:
          'Cada contenedor con 30 días o más desde la ETA y sin liquidación aprobada, del más antiguo al más reciente.',
      },
      {
        key: 'settlementsDueSoon',
        label: 'Detalle por contenedor: por vencer',
        description:
          'Cada contenedor sin liquidar que cumple el plazo de 30 días durante la próxima semana.',
      },
    ],
  },
];

const SEASON_PERIOD: ReportPeriod = { kind: 'season' };
// Nombre de la especie sin emoji ("🍒 Cherry" → "Cherry"): va al reporte, al
// título y al nombre del PDF, donde el emoji puede salir como un cuadro.
const plainSpeciesName = (name: string) =>
  name.replace(/[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu, '').trim();
const AUTO_WEEK = 'auto';

const EMPTY_DATA: ClientReportData = {
  boxes: [],
  settledReferences: new Set(),
  program: null,
  commitments: [],
};

// La vista previa se dibuja dentro de una ventana de navegador con el mismo
// marco del link, en ancho de computador o de celular.
const PreviewWindow = ({
  device,
  children,
}: {
  device: PreviewDevice;
  children: React.ReactNode;
}) => (
  <div
    className={device === 'mobile' ? 'mx-auto w-full max-w-[390px]' : 'w-full'}
  >
    <div className='overflow-hidden rounded-xl border border-default-200 shadow-sm'>
      <div className='flex items-center gap-3 border-b border-default-200 bg-default-100 px-3 py-2'>
        <div className='flex gap-1.5'>
          <span className='h-2.5 w-2.5 rounded-full bg-default-300' />
          <span className='h-2.5 w-2.5 rounded-full bg-default-300' />
          <span className='h-2.5 w-2.5 rounded-full bg-default-300' />
        </div>
        <div className='min-w-0 flex-1 truncate rounded-md bg-background px-3 py-1 text-xs text-default-500'>
          {window.location.host}/r/…
        </div>
      </div>
      <div className='max-h-[75vh] overflow-y-auto'>
        <ReportLayoutContext.Provider
          value={device === 'mobile' ? 'mobile' : 'responsive'}
        >
          <SharedReportFrame fullHeight={false}>
            <SharedReportCard>{children}</SharedReportCard>
          </SharedReportFrame>
        </ReportLayoutContext.Provider>
      </div>
    </div>
  </div>
);

const NewClientReportPage = () => {
  const { t } = useTranslation('reports');
  const [, setLocation] = useLocation();
  const { userProfile, session } = useUserStore();
  // Temporada y especie son las del menú lateral; el formulario las muestra.
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;

  const [data, setData] = useState<ClientReportData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [selectedReceivers, setSelectedReceivers] = useState<string[]>([]);
  const [reportKind, setReportKind] = useState<ReportKind>('shipments');
  const [parts, setParts] = useState<ReportSectionType[]>(
    REPORT_KINDS[0].parts.map((part) => part.key)
  );
  const [linkMode, setLinkMode] = useState<LinkMode>('perReceiver');
  const [compareWithRest, setCompareWithRest] = useState(false);
  const [previewReceiver, setPreviewReceiver] = useState<string>('');
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [isGenerating, setIsGenerating] = useState(false);
  const [createdReports, setCreatedReports] = useState<SharedReport[]>([]);

  useEffect(() => {
    if (!exporterId || !selectedSeason || !selectedSpecies?.id || !session) {
      return;
    }
    let cancelled = false;
    const speciesId = selectedSpecies.id;

    const load = async () => {
      setLoading(true);
      setData(EMPTY_DATA);
      setSelectedReceivers([]);
      try {
        const receivers = await getReceiversByExporter(exporterId);
        const loaded = await loadClientReportData({
          exporterId,
          season: selectedSeason,
          speciesId,
          receiverNameById: new Map(
            receivers.map((receiver) => [Number(receiver.id), receiver.name])
          ),
        });
        if (!cancelled) setData(loaded);
      } catch (error) {
        console.error('Error loading report data:', error);
        if (!cancelled) toast.error(t('No se pudieron cargar los despachos'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // userProfile y session cambian de referencia al refrescar el token; los
    // datos dependen solo de la exportadora, la temporada y la especie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporterId, selectedSeason, selectedSpecies?.id]);

  const receiverOptions = useMemo(() => {
    const kilosByReceiver = new Map<string, number>();
    for (const box of data.boxes) {
      const receiver = box.receiver?.trim();
      if (!receiver) continue;
      kilosByReceiver.set(
        receiver,
        (kilosByReceiver.get(receiver) ?? 0) +
          (box.quantity || 0) * (box.box_weight || 0)
      );
    }
    return Array.from(kilosByReceiver, ([receiver, kilos]) => ({
      value: receiver,
      label: receiver,
      kilos,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const kind = REPORT_KINDS.find((item) => item.key === reportKind)!;
  const changeReportKind = (key: ReportKind) => {
    setReportKind(key);
    setParts(
      REPORT_KINDS.find((item) => item.key === key)!.parts.map(
        (part) => part.key
      )
    );
  };
  const period = SEASON_PERIOD;
  // En el orden del informe, no en el que se marcaron.
  const sections = useMemo(
    () =>
      kind.parts.map((part) => part.key).filter((key) => parts.includes(key)),
    [kind, parts]
  );

  // Cada link es un grupo de clientes: uno por cliente, o todos juntos.
  const linkGroups = useMemo(
    () =>
      linkMode === 'single' || selectedReceivers.length <= 1
        ? selectedReceivers.length
          ? [selectedReceivers]
          : []
        : selectedReceivers.map((receiver) => [receiver]),
    [linkMode, selectedReceivers]
  );

  useEffect(() => {
    if (!selectedReceivers.includes(previewReceiver)) {
      setPreviewReceiver(selectedReceivers[0] || '');
    }
  }, [previewReceiver, selectedReceivers]);

  const previewGroup =
    linkGroups.length > 1
      ? linkGroups.find((group) => group[0] === previewReceiver) ||
        linkGroups[0]
      : linkGroups[0];

  // Semana de corte: por defecto la última semana completa con zarpes; se
  // puede elegir otra.
  const { dayRange, date } = useReportFormat();
  const kgPerCeq = selectedSpecies?.default_unit_price || 5;
  const [recentWeekStart, setRecentWeekStart] = useState<string>(AUTO_WEEK);
  const departureWeeks = useMemo(
    () =>
      listReportDepartureWeeks({
        data,
        receivers: selectedReceivers,
        kgPerCeq,
        today: new Date(),
      }),
    [data, selectedReceivers, kgPerCeq]
  );
  // El aviso de base desactualizada y "Hasta hoy" son solo para la temporada
  // en curso: en una pasada, que no haya embarques nuevos es lo esperable.
  const seasonInProgress = useMemo(() => {
    if (!selectedSpecies || !selectedSeason) return false;
    const weeks = getSeasonWeeksForSpecies(selectedSpecies.id, selectedSeason);
    const index = getSeasonCutoffIndex({ weeks, selectedSeason });
    return index >= 0 && index < weeks.length - 1;
  }, [selectedSpecies, selectedSeason]);
  // "Hasta hoy": la semana en curso, aunque todavía no tenga zarpes.
  const todayWeekStart = currentWeekStart(new Date());
  useEffect(() => {
    if (
      recentWeekStart !== AUTO_WEEK &&
      !(seasonInProgress && recentWeekStart === todayWeekStart) &&
      !departureWeeks.some((week) => week.start === recentWeekStart)
    ) {
      setRecentWeekStart(AUTO_WEEK);
    }
  }, [departureWeeks, recentWeekStart, todayWeekStart, seasonInProgress]);
  // Corte del acumulado y del cumplimiento a la fecha; también es la semana
  // de "Última semana" y de "Últimos embarques despachados".
  const showsRecentWeek = sections.some(
    (section) => section === 'shipmentsToDate' || section === 'recentShipments'
  );
  const effectiveRecentWeek =
    recentWeekStart !== AUTO_WEEK ? recentWeekStart : null;
  const defaultWeek = defaultDepartureWeek(departureWeeks, new Date());
  const staleBase = useMemo(
    () => (seasonInProgress ? staleShipmentsBase(data.boxes, new Date()) : null),
    [data, seasonInProgress]
  );
  const recentWeekOptions = defaultWeek
    ? [
        {
          key: AUTO_WEEK,
          label: t(
            defaultWeek.end < localDay(new Date())
              ? 'La última semana completa (sem {{week}})'
              : 'La última con zarpes (sem {{week}})',
            { week: String(defaultWeek.week).padStart(2, '0') }
          ),
        },
        ...(seasonInProgress
          ? [
              {
                key: todayWeekStart,
                label: t('Hasta hoy (sem {{week}})', {
                  week: String(weekOfDate(todayWeekStart) ?? '').padStart(2, '0'),
                }),
              },
            ]
          : []),
        ...departureWeeks
          .filter((week) => !seasonInProgress || week.start !== todayWeekStart)
          .map((week) => ({
            key: week.start,
            label: t('Sem {{week}} · {{range}} · {{shipments}} embarques', {
              week: String(week.week).padStart(2, '0'),
              range: dayRange(week.start, week.end),
              shipments: week.shipments,
            }),
          })),
      ]
    : [];

  const buildSnapshot = (receivers: string[], generatedAt: Date) =>
    buildClientReportSnapshot({
      kind: reportKind,
      data,
      receivers,
      period,
      sections,
      exporterName: userProfile?.exporter?.name || '',
      exporterLogoUrl: userProfile?.exporter?.logo_url || null,
      speciesId: selectedSpecies!.id,
      speciesName: plainSpeciesName(selectedSpecies!.name),
      season: selectedSeason,
      kgPerCeq,
      generatedAt,
      recentWeekStart: effectiveRecentWeek,
      caliberOrder: selectedSpecies?.calibers ?? null,
      compareWithRest,
    });

  const preview = useMemo(
    () =>
      previewGroup && period && sections.length && selectedSpecies
        ? buildSnapshot(previewGroup, new Date())
        : null,
    // buildSnapshot solo lee estado que ya está en las dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      reportKind,
      previewGroup,
      sections,
      data,
      selectedSpecies,
      effectiveRecentWeek,
      compareWithRest,
    ]
  );

  const canGenerate = Boolean(
    exporterId && linkGroups.length && sections.length && !loading
  );

  // El PDF sale de la misma foto que el link, sin guardar nada: sirve para
  // mandarlo por correo o adjuntarlo. Con un link por cliente, un PDF por cliente.
  const { download: downloadPdf, downloading: downloadingPdf } =
    useDownloadReportPdf();
  const handleDownloadPdf = () => {
    if (!canGenerate) return;
    const generatedAt = new Date();
    downloadPdf(
      linkGroups.map((receivers) => buildSnapshot(receivers, generatedAt))
    );
  };

  const handleGenerate = async () => {
    if (!canGenerate || !exporterId) return;
    setIsGenerating(true);
    try {
      const generatedAt = new Date();
      const speciesLabel = `${t(plainSpeciesName(selectedSpecies!.name))} ${selectedSeason}`;
      const kindLabel = t(kind.label);
      const created = await createSharedReports(
        linkGroups.map((receivers) => ({
          exporter_id: exporterId,
          created_by: userProfile?.id ?? null,
          title:
            receivers.length === 1
              ? `${kindLabel} · ${receivers[0]} · ${speciesLabel}`
              : `${kindLabel} · ${t('{{count}} clientes', { count: receivers.length })} · ${speciesLabel}`,
          season: selectedSeason,
          species_id: selectedSpecies!.id,
          receivers,
          sections,
          period,
          snapshot: buildSnapshot(receivers, generatedAt),
        }))
      );
      setCreatedReports(created);
    } catch (error) {
      console.error('Error creating shared reports:', error);
      toast.error(t('No se pudo generar el reporte'));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyLink = async (token: string) => {
    await navigator.clipboard.writeText(getSharedReportUrl(token));
    toast.success(t('Link copiado'));
  };

  return (
    <PageLayout>
      <BackButton href='/client-reports' label={t('Volver a Reportes')} />
      <Header
        title={t('Nuevo reporte')}
        description={t(
          'Elige el informe, los clientes y las secciones. El link guarda una foto de los datos de hoy: lo que ves en la vista previa es lo que verá el cliente.'
        )}
      />

      <div className='grid gap-4 lg:grid-cols-[360px_1fr]'>
        <Card shadow='sm' className='h-fit'>
          <CardBody className='gap-5'>
            <Select
              label={t('Informe')}
              selectedKeys={[reportKind]}
              disallowEmptySelection
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as ReportKind | undefined;
                if (key) changeReportKind(key);
              }}
            >
              {REPORT_KINDS.map((item) => (
                <SelectItem key={item.key}>{t(item.label)}</SelectItem>
              ))}
            </Select>

            {/* Por ahora, un cliente por reporte. */}
            <Select
              label={t('Cliente')}
              placeholder={t('Selecciona un cliente')}
              selectedKeys={selectedReceivers.slice(0, 1)}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0];
                setSelectedReceivers(key ? [String(key)] : []);
              }}
              items={receiverOptions}
              isLoading={loading}
              isDisabled={loading || receiverOptions.length === 0}
              description={
                !loading && receiverOptions.length === 0
                  ? t(
                      'No hay despachos de {{species}} en la temporada {{season}}',
                      {
                        species: t(selectedSpecies?.name || ''),
                        season: selectedSeason,
                      }
                    )
                  : undefined
              }
            >
              {(option) => (
                <SelectItem key={option.value} textValue={option.label}>
                  <div className='flex w-full items-center justify-between gap-3'>
                    <span>{option.label}</span>
                    <span className='text-xs text-default-400'>
                      {Math.round(option.kilos).toLocaleString()} kg
                    </span>
                  </div>
                </SelectItem>
              )}
            </Select>

            {showsRecentWeek && departureWeeks.length > 0 ? (
              <Select
                label={t('Última semana a considerar')}
                description={t(
                  'Corte del reporte: el acumulado y el cumplimiento a la fecha llegan hasta esta semana.'
                )}
                selectedKeys={[recentWeekStart]}
                disallowEmptySelection
                onSelectionChange={(keys) => {
                  const key = Array.from(keys)[0];
                  if (key) setRecentWeekStart(String(key));
                }}
                items={recentWeekOptions}
              >
                {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
              </Select>
            ) : null}

            {staleBase ? (
              <Alert variant='warning' showIcon>
                {t(
                  'El último embarque cargado en Despachos zarpó el {{date}}, hace {{days}} días. Si faltan despachos por cargar, el reporte va a quedar incompleto.',
                  { date: date(staleBase.latestEtd), days: staleBase.days }
                )}
              </Alert>
            ) : null}

            {selectedReceivers.length > 1 ? (
              <RadioGroup
                label={t('Links')}
                value={linkMode}
                onValueChange={(value) => setLinkMode(value as LinkMode)}
              >
                <Radio
                  value='perReceiver'
                  description={t('Cada cliente ve solo sus datos')}
                >
                  {t('Un link por cliente ({{count}})', {
                    count: selectedReceivers.length,
                  })}
                </Radio>
                <Radio
                  value='single'
                  description={t('Todos los clientes en el mismo reporte')}
                >
                  {t('Un solo link con todos')}
                </Radio>
              </RadioGroup>
            ) : null}

            <CheckboxGroup
              label={t('Secciones')}
              value={parts}
              onValueChange={(value) => setParts(value as ReportSectionType[])}
            >
              {kind.parts.map((part) => (
                <Checkbox
                  key={part.key}
                  value={part.key}
                  classNames={{
                    base: 'max-w-none items-start',
                    wrapper: 'mt-0.5',
                  }}
                >
                  <span className='flex flex-col gap-0.5'>
                    <span className='text-sm'>{t(part.label)}</span>
                    <span className='text-xs text-default-500'>
                      {t(part.description)}
                    </span>
                  </span>
                </Checkbox>
              ))}
            </CheckboxGroup>

            {sections.includes('shipmentsToDate') && data.program ? (
              <Checkbox
                isSelected={compareWithRest}
                onValueChange={setCompareWithRest}
                classNames={{ base: 'max-w-none items-start', wrapper: 'mt-0.5' }}
              >
                <span className='flex flex-col gap-0.5'>
                  <span className='text-sm'>
                    {t('Comparar con el resto de los clientes')}
                  </span>
                  <span className='text-xs text-default-500'>
                    {t(
                      'Junto al cumplimiento del plan, muestra cuánto se le ha cumplido al resto de los clientes con plan, sin nombres.'
                    )}
                  </span>
                </span>
              </Checkbox>
            ) : null}

            <div className='flex flex-col gap-2'>
              <Button
                color='primary'
                isDisabled={!canGenerate}
                isLoading={isGenerating}
                onPress={handleGenerate}
              >
                {linkGroups.length > 1
                  ? t('Generar {{count}} links', { count: linkGroups.length })
                  : t('Generar link')}
              </Button>
              <Button
                variant='flat'
                isDisabled={!canGenerate}
                isLoading={downloadingPdf}
                startContent={
                  downloadingPdf ? null : (
                    <Icon icon='tabler:file-type-pdf' width={18} />
                  )
                }
                onPress={handleDownloadPdf}
              >
                {linkGroups.length > 1
                  ? t('Descargar {{count}} PDF', { count: linkGroups.length })
                  : t('Descargar PDF')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card shadow='sm'>
          <CardBody className='gap-4'>
            <div className='flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
              <div>
                <h3 className='text-lg font-semibold'>{t('Vista previa')}</h3>
                <p className='text-sm text-default-500'>
                  {t('Así lo verá tu cliente al abrir el link.')}
                </p>
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                {linkGroups.length > 1 ? (
                  <Select
                    aria-label={t('Ver cliente')}
                    size='sm'
                    className='w-56'
                    selectedKeys={previewReceiver ? [previewReceiver] : []}
                    onChange={(event) =>
                      event.target.value &&
                      setPreviewReceiver(event.target.value)
                    }
                  >
                    {selectedReceivers.map((receiver) => (
                      <SelectItem key={receiver}>{receiver}</SelectItem>
                    ))}
                  </Select>
                ) : null}
                <Tabs
                  aria-label={t('Dispositivo')}
                  size='sm'
                  selectedKey={previewDevice}
                  onSelectionChange={(key) =>
                    setPreviewDevice(key as PreviewDevice)
                  }
                >
                  <Tab
                    key='desktop'
                    title={
                      <span className='flex items-center gap-1'>
                        <Icon icon='tabler:device-desktop' />
                        {t('Computador')}
                      </span>
                    }
                  />
                  <Tab
                    key='mobile'
                    title={
                      <span className='flex items-center gap-1'>
                        <Icon icon='tabler:device-mobile' />
                        {t('Celular')}
                      </span>
                    }
                  />
                </Tabs>
              </div>
            </div>
            {loading ? (
              <LoadingState label={t('Cargando despachos...')} />
            ) : preview ? (
              <PreviewWindow device={previewDevice}>
                <ReportDocument snapshot={preview} />
              </PreviewWindow>
            ) : (
              <EmptyState
                icon='tabler:file-analytics'
                title={t(
                  'Elige al menos un cliente y una sección para ver el reporte.'
                )}
              />
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        isOpen={createdReports.length > 0}
        onClose={() => setLocation('/client-reports')}
        size='2xl'
      >
        <ModalContent>
          <ModalHeader>
            {createdReports.length > 1
              ? t('Se generaron {{count}} links', {
                  count: createdReports.length,
                })
              : t('Link generado')}
          </ModalHeader>
          <ModalBody className='gap-3'>
            {createdReports.map((report) => (
              <div
                key={report.id}
                className='flex flex-col gap-2 rounded-lg border border-default-200 p-3 md:flex-row md:items-center'
              >
                <div className='min-w-0 flex-1'>
                  <p className='truncate font-medium'>{report.title}</p>
                  <p className='truncate text-xs text-default-500'>
                    {getSharedReportUrl(report.share_token)}
                  </p>
                </div>
                <Button
                  size='sm'
                  variant='flat'
                  onPress={() => copyLink(report.share_token)}
                >
                  {t('Copiar link')}
                </Button>
              </div>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button
              color='primary'
              onPress={() => setLocation('/client-reports')}
            >
              {t('Ir a Reportes')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default NewClientReportPage;
